import { privateApi } from '@/config/axios.config';
import { TaskFilters, Task, TaskCreateDTO, TaskUpdateDTO, TaskStats } from '../types/tasks.types';

const API_BASE = '/agency/tasks';

export interface TaskResponse {
  data: Task[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const tasksApiService = {
  /**
   * Obtener todas las tareas con filtros y paginación
   */
  async getTasks(filters: TaskFilters = {}): Promise<TaskResponse> {
    const params = new URLSearchParams();

    if (filters.status) params.append('status', filters.status);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.type) params.append('type', filters.type);
    if (filters.assignedToId) params.append('assignedToId', filters.assignedToId);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const url = `${API_BASE}?${params.toString()}`;
    
    try {
      const response = await privateApi.get(url);
      
      // El ResponseTransformInterceptor envuelve la respuesta así:
      // { success: true, data: { data: [...], page, limit, total, totalPages }, pagination: {...} }
      
      let result = response.data;
      
      // Si hay un wrapper con success y data, extraer el data interno
      if (response.data?.success && response.data?.data) {
        const innerData = response.data.data;
        
        // Si innerData tiene estructura de paginación (con data, page, limit, etc.)
        if (innerData && typeof innerData === 'object' && 'total' in innerData && 'page' in innerData) {
          // Usar innerData directamente ya que tiene la estructura correcta
          result = {
            data: innerData.data || innerData, // Asegurar que data sea un array
            page: innerData.page || response.data.pagination?.page || 1,
            limit: innerData.limit || response.data.pagination?.limit || 10,
            total: innerData.total || response.data.pagination?.total || 0,
            totalPages: innerData.totalPages || response.data.pagination?.totalPages || 0,
          };
        } else {
          // Si innerData es un array o no tiene paginación, usar pagination del wrapper
          result = {
            data: Array.isArray(innerData) ? innerData : (innerData?.data || []),
            page: response.data.pagination?.page || 1,
            limit: response.data.pagination?.limit || 10,
            total: response.data.pagination?.total || 0,
            totalPages: response.data.pagination?.totalPages || 0,
          };
        }
      } else if (response.data?.data && typeof response.data.data === 'object' && !Array.isArray(response.data.data)) {
        // Si response.data.data existe y es un objeto (no array), podría tener paginación interna
        const innerData = response.data.data;
        if ('total' in innerData && 'page' in innerData) {
          result = {
            data: innerData.data || [],
            page: innerData.page || 1,
            limit: innerData.limit || 10,
            total: innerData.total || 0,
            totalPages: innerData.totalPages || 0,
          };
        } else {
          result = response.data.data;
        }
      } else {
        // Usar response.data directamente (fallback)
        result = {
          data: Array.isArray(response.data) ? response.data : (response.data?.data || []),
          page: response.data?.page || 1,
          limit: response.data?.limit || 10,
          total: response.data?.total || 0,
          totalPages: response.data?.totalPages || 0,
        };
      }
      
      return result;
    } catch (error: any) {
      throw error;
    }
  },

  /**
   * Obtener una tarea por ID
   */
  async getTaskById(id: string): Promise<Task> {
    const response = await privateApi.get(`${API_BASE}/${id}`);
    return response.data.data;
  },

  /**
   * Crear una nueva tarea
   */
  async createTask(data: TaskCreateDTO): Promise<Task> {
    const response = await privateApi.post(API_BASE, data);
    return response.data.data;
  },

  /**
   * Actualizar una tarea
   */
  async updateTask(id: string, data: TaskUpdateDTO): Promise<Task> {
    const response = await privateApi.patch(`${API_BASE}/${id}`, data);
    return response.data.data;
  },

  /**
   * Eliminar una tarea (soft delete)
   */
  async deleteTask(id: string): Promise<void> {
    await privateApi.delete(`${API_BASE}/${id}`);
  },

  /**
   * Cambiar el estado de una tarea
   */
  async changeStatus(id: string, status: string): Promise<Task> {
    const response = await privateApi.patch(`${API_BASE}/${id}/status`, { status });
    return response.data.data;
  },

  /**
   * Cambiar la prioridad de una tarea
   */
  async changePriority(id: string, priority: string): Promise<Task> {
    const response = await privateApi.patch(`${API_BASE}/${id}/priority`, { priority });
    return response.data.data;
  },

  /**
   * Reasignar una tarea a otro usuario
   */
  async reassignTask(id: string, assignedToId: string): Promise<Task> {
    const response = await privateApi.patch(`${API_BASE}/${id}/reassign`, { assignedToId });
    return response.data.data;
  },

  /**
   * Obtener estadísticas de tareas
   */
  async getStats(): Promise<TaskStats> {
    const response = await privateApi.get(`${API_BASE}/stats`);
    return response.data.data;
  },

  /**
   * Obtener tareas vencidas
   */
  async getOverdueTasks(): Promise<Task[]> {
    const response = await privateApi.get(`${API_BASE}/overdue`);
    return response.data.data;
  },
};

export default tasksApiService;



